import BaseWidget from "common/widgets/base/BaseWidget";
import stringUtil from "common/helper/stringUtil";
import classes from "app/css/widgets/TextArea.module.less"

const TextArea = BaseWidget(kendo.ui.Widget).extend({

    options: {
        name: "TextArea",

        /** The max length of the text area. */
        maxlength: null,

        /** Initial readonly state of widget. */
        readonly: false,

        /** Initial enabled state of the widget. */
        enabled: true,

        // if provided, specifies upper or lower case
        characterCase: null,

        // prevent the enter key on the text area
        preventEnter: false
    },

    events: [
        "input",
        "change"
    ],

    init: function() {
        kendo.ui.Widget.fn.init.apply(this, arguments);
        this._maxLength();
        this._render();
        this.wrapper.addClass(classes.TextArea);
        this.enable(this.options.enabled);
        this.readonly(this.options.readonly);
        this.element.bind("input", $.proxy(this._inputChange, this));
        this.element.bind("change", $.proxy(this._valueChange, this));
        this.element.on("keydown", this._keydown.bind(this));

        if (this.options.characterCase) {
            if (this.options.characterCase === "UPPER") {
                this.element.css("text-transform", "uppercase");
            } else if (this.options.characterCase === "LOWER") {
                this.element.css("text-transform", "lowercase");
            }
        }
    },

    /**
     * Cleanup the widget.
     */
    destroy: function() {
        kendo.ui.Widget.fn.destroy.apply(this, arguments);
        this.element.off();
        this.wrapper && this.wrapper.remove();
        delete this.wrapper;
        delete this.notification;
    },

    /**
     * Setup the basic structure of the widget.
     * @private
     */
    _render: function() {
        this.element.wrap("<div>");
        this.wrapper = this.element.parent();
        this.notification = $("<span>").addClass("notification").hide();
        this.wrapper.append(this.notification);
    },

    /**
     * Key down event handler
     * @param e
     * @private
     */
    _keydown: function(e) {
        this._lastCaret = kendo.caret(this.element);
        if (this.options.preventEnter && e.keyCode === kendo.keys.ENTER) {
            e.preventDefault();
            // we must trigger a value change
            this._valueChange();
        }
    },

    /**
     * Setup the maxlength properties of the widget.
     * @private
     */
    _maxLength: function() {
        this.options.maxlength = this.options.maxlength || this.element.attr("maxlength");
        this.element.removeAttr("maxlength");
    },

    /**
     * Check the max length and update the notification display.
     * @private
     */
    _updateNotification: function() {
        if (this.options.maxlength) {
            var val = this.element.val();
            var now = val ? stringUtil.length(val) : 0;
            var max = this.options.maxlength;
            if (now / max >= .7) {
                this.notification.text(max - now).show();
            } else {
                this.notification.text("").hide();
            }
        }
    },

    /**
     * Handle user typing.
     * @private
     */
    _inputChange: function() {
        if(this.isValueValid()) {
            this._value = this.element.val();
            this._updateNotification();
            this.trigger("input");
        } else {
            this._restoreValidValue();
        }
        delete this._lastCaret;
    },

    /**
     * Handle change on input.
     * @private
     */
    _valueChange: function() {
        this._currentValue = this.element.val();
        this.trigger("change");
    },

    _restoreValidValue() {
        this.element.val(this._value);
        if(this._lastCaret) {
            kendo.caret(this.element, this._lastCaret[0], this._lastCaret[1]);
        }
    },

    /**
     * The current value of the widget.
     */
    _currentValue: "",

    /**
     * Value getter/setter.
     */
    value: function(val) {
        if (arguments.length) {
            this._currentValue = this._value = val;
            if (val === null || val === undefined) {
                val = "";
            }
            this.element.val(val);
            this._updateNotification();
        } else {
            return this._currentValue;
        }
    },

    isValueValid() {
        const val = this.element.val();
        return !this.options.maxlength || stringUtil.length(val) <= this.options.maxlength;
    },

    /**
     * Set the enabled state of the widget.
     */
    enable: function(to) {
        this._enabled = to ? true : false;
        if (this._enabled) {
            this.element.removeAttr("disabled");
        } else {
            this.element.attr("disabled", true);
        }
    },

    /**
     * Set the readonly state fo the widget.
     */
    readonly: function(to) {
        this._readonly = to ? true : false;
        if (this._readonly) {
            this.wrapper.addClass("readMode");
            this.wrapper.removeClass("editMode");
        } else {
            this.wrapper.addClass("editMode");
            this.wrapper.removeClass("readMode");
        }
    },

    /**
     * Focus the textarea.
     */
    focus: function() {
        this.element.focus();
    },

    /**
     * Insert text into the textarea.
     *
     * The text will be inserted after the cursor or after the selected text.
     * This relies on the selection range api.  The browser will remember cursor position.
     * This makes no attempt to smooth browsers differences in cursor behavior.
     *
     * Should provide Undo/Redo support but that is a best effort attempt due
     * to browser inconsistencies.
     *
     * @param text
     */
    insertText: function(text) {
        return this._insertText(text, function(before, start, end) {
            return {
                after: start,
                before: end
            }
        });
    },

    /**
     * Replace all current text.
     * This is similiar to value() setter, execept it fires change events.
     * @param text
     * @returns {*}
     */
    replaceText: function(text) {
        return this._insertText(text, function(before) {
            return {
                after: 0,
                before: before.length
            }
        });
    },

    /**
     * Insert text into the textarea.
     *
     * The text will be inserted over the selected text.  If adjusting the selection slightly will cause prefix/postfix to be re-used, that will be done.
     * @param text
     * @param prefix
     * @param postfix
     * @returns {*}
     */
    insertTextAccountForWrap: function(text, prefix, postfix) {
        return this._insertText(text, function(before, start, end) {
            var textPre = before.slice(0, start);
            var textPost = before.slice(end);
            if (stringUtil.startsWith(textPost, postfix) && stringUtil.endsWith(textPre, prefix)) {
                return {
                    after: start - prefix.length,
                    before: end + postfix.length
                }
            } else {
                return {
                    after: start,
                    before: end
                }
            }
        });
    },


    /**
     * Core insertion logic. Has the ability to have the ranges in which insertion happens be adjusted.
     * @param text
     * @param adjustRanges
     * @private
     */
    _insertText: function(text, adjustRanges) {
        var before = this.element.val();
        var insert = adjustRanges(before, this.element[0].selectionStart, this.element[0].selectionEnd);


        var after = before.slice(0, insert.after) + text + before.slice(insert.before);
        this.element.val(after);

        //update model
        this._inputChange();
        this._valueChange();

        //Highlight inserted text
        this.element.focus();
        this.element[0].setSelectionRange(insert.after, insert.after + text.length);
    }
});

kendo.ui.plugin(TextArea);
export default TextArea;
